import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import localeEs from '@angular/common/locales/es';
import { APP_INITIALIZER, ApplicationConfig, LOCALE_ID, provideZoneChangeDetection } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { PreloadAllModules, provideRouter, withPreloading, withRouterConfig } from '@angular/router';
import { ModalController } from '@ionic/angular';
import { provideIonicAngular } from '@ionic/angular/standalone';
import { includeBearerTokenInterceptor, provideKeycloak } from 'keycloak-angular';
import { RECAPTCHA_V3_SITE_KEY } from 'ng-recaptcha-2';
import { environment } from 'src/environments/environment';
import { NGX_TIPPY_CONFIG } from 'ngx-tippy-wrapper';
import { provideToastr } from 'ngx-toastr';
import { AuthService, ErrorInterceptor, initializeKeycloak } from 'src/app/core';
import { CmsLobbyChannelInterceptor } from 'src/app/core/interceptors/cms-lobby-channel.interceptor';
import { AppConfig } from 'src/app-config.model';
import { register } from 'swiper/element/bundle';
import { appRoutes } from './app.routes';
import { NavInterceptor } from './shared';
import { GameApiService } from './shared/services/data/games/gameApi.service';
import { EnvironmentService } from './shared/utils/environment.util';
import { ContentDataSource } from './shared/services/institutional/content-data-source';
import { JsonContentDataSource } from './shared/services/institutional/json-content-data-source';
import { CmsLocalesDataSource } from './shared/services/institutional/cms-locales-data-source';
import { HelpContentDataSource } from './shared/services/help/help-content-data-source';
import { JsonHelpContentDataSource } from './shared/services/help/json-help-content-data-source';
import { ApiHelpContentDataSource } from './shared/services/help/api-help-content-data-source';
import { PoliticasContentDataSource } from './shared/services/politicas/politicas-content-data-source';
import { JsonPoliticasContentDataSource } from './shared/services/politicas/json-politicas-content-data-source';
import { JuegoResponsableContentDataSource } from './shared/services/juego-responsable/juego-responsable-content-data-source';
import { JsonJuegoResponsableContentDataSource } from './shared/services/juego-responsable/json-juego-responsable-content-data-source';
import { RaspaditasFisicasContentDataSource } from './shared/services/raspaditas-fisicas/raspaditas-fisicas-content-data-source';
import { CmsRaspaditasFisicasContentDataSource } from './shared/services/raspaditas-fisicas/cms-raspaditas-fisicas-content-data-source';
import { CabalasContentDataSource } from './shared/services/cabalas/cabalas-content-data-source';
import { JsonCabalasContentDataSource } from './shared/services/cabalas/json-cabalas-content-data-source';

register();
registerLocaleData(localeEs, 'es');

export function appConfig(config: AppConfig): ApplicationConfig {
  return {
    providers: [
      { provide: LOCALE_ID, useValue: 'es' },
      provideKeycloak(initializeKeycloak(config)),
      provideHttpClient(withInterceptors([includeBearerTokenInterceptor]), withInterceptorsFromDi()),
      {
        provide: HTTP_INTERCEPTORS,
        useClass: ErrorInterceptor,
        multi: true,
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: CmsLobbyChannelInterceptor,
        multi: true,
      },
      EnvironmentService,
      {
        provide: APP_INITIALIZER,
        useFactory: (envService: EnvironmentService) => () => envService.applyConfig(config),
        deps: [EnvironmentService],
        multi: true,
      },
      {
        provide: RECAPTCHA_V3_SITE_KEY,
        useValue: environment.recaptchaConfig.siteKey,
      },

      provideZoneChangeDetection({ eventCoalescing: true }),
      provideRouter(appRoutes, withRouterConfig({ onSameUrlNavigation: 'ignore' }), withPreloading(PreloadAllModules)),
      provideIonicAngular({
        rippleEffect: true,
        mode: 'ios',
        animated: true,
        innerHTMLTemplatesEnabled: true,
      }),
      AuthService,
      {
        provide: NGX_TIPPY_CONFIG,
        useValue: {
          delay: [0, 100],
          theme: 'light',
          arrow: false,
          animation: 'shift-away-subtle',
        },
      },
      provideToastr({ preventDuplicates: true }),
      provideAnimations(),
      NavInterceptor,
      ModalController,
      GameApiService,
      // Institutional content - CmsLocalesDataSource merges live CMS catalog with static editorial JSON
      JsonContentDataSource,
      { provide: ContentDataSource, useClass: CmsLocalesDataSource },
      // Help content - ApiHelpContentDataSource calls CMS API and falls back to JSON
      JsonHelpContentDataSource,
      { provide: HelpContentDataSource, useClass: ApiHelpContentDataSource },
      // Politicas content - swap JsonPoliticasContentDataSource for CMS when ready
      { provide: PoliticasContentDataSource, useClass: JsonPoliticasContentDataSource },
      // Juego Responsable content - swap JsonJuegoResponsableContentDataSource for CMS when ready
      { provide: JuegoResponsableContentDataSource, useClass: JsonJuegoResponsableContentDataSource },
      // Raspaditas Físicas content — served from CMS (BQM-295)
      { provide: RaspaditasFisicasContentDataSource, useClass: CmsRaspaditasFisicasContentDataSource },
      // Cabalas content - swap JsonCabalasContentDataSource for CMS when ready
      { provide: CabalasContentDataSource, useClass: JsonCabalasContentDataSource },
    ],
  };
}
